import { API_BASE_URL } from "@/lib/constants/api";
import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import { toast } from "sonner";
import { useAuthStore } from "@/store/authStore";
import { endSession } from "@/lib/auth/endSession";
import { useContextStore } from "@/store/contextStore";
import { extractErrorCode } from "@/lib/api/errorCodes";
import { isOrganizationId } from "@/lib/utils";
import {
  isScopeRefusal,
  refuseScopeFromServer,
  type SentScope,
} from "@/lib/scope/orgParam";

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * The scope pair each request was stamped with from the store, so a refusal
 * can be traced back to the selection that caused it. Keyed by the config
 * object, which axios hands back on the error; nothing is added to the config
 * that a caller could see.
 */
const stampedFromBar = new WeakMap<InternalAxiosRequestConfig, SentScope>();

/** Add the auth token and the scope headers to an outgoing request. */
function stamp(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const { accessToken } = useAuthStore.getState();

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  // Impersonation is enforced via JWT claims from POST /auth/impersonate/ (access token).
  // Do not send X-Impersonate-User here: the backend treats that header as a target user id,
  // which would conflict with token-based impersonation.

  // Add context scope headers for admin filtering (Distributor / MSP / Company)
  //
  // A caller that has already set `X-Context-Scope` wins. That escape hatch
  // exists for *navigators*: a request whose job is to list what you could
  // select must not be narrowed by what you have already selected. Scoping
  // the org lists to the selected company returns zero distributors and zero
  // MSPs, which emptied the tree in the credentials sidebar the moment you
  // picked a company in it — the control deleted itself.
  if (config.headers?.["X-Context-Scope"]) {
    return config;
  }

  const { scope, distributorId, mspId, companyId } = useContextStore.getState();
  if (scope && scope !== "default") {
    const contextId =
      scope === "distributor"
        ? distributorId
        : scope === "msp"
          ? mspId
          : scope === "company"
            ? companyId
            : null;

    // Organization ids are UUIDs, and the server answers any other
    // `X-Context-Id` with a 400 that fails the whole request. The store
    // drops such ids when it loads, but a value set at runtime is checked
    // here too, so a bad one costs the scope header rather than the page.
    if (contextId && isOrganizationId(contextId)) {
      config.headers["X-Context-Scope"] = scope;
      config.headers["X-Context-Id"] = contextId;
      stampedFromBar.set(config, { scope, id: contextId });
    } else if (!contextId) {
      config.headers["X-Context-Scope"] = scope;
      stampedFromBar.set(config, { scope, id: null });
    }
  }

  return config;
}

// Request interceptor - add auth token and context scope headers
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    try {
      return stamp(config);
    } catch (error) {
      // Run synchronously, a throw here does not fail the request: axios hands
      // it to the rejection handler and sends the request on, unstamped. It is
      // refused instead, as it was when this ran inside the promise chain.
      config.adapter = () => Promise.reject(error);
      return config;
    }
  },
  (error) => Promise.reject(error),
  // Synchronous, so the store is read in the call itself rather than a
  // microtask later. A query's key is built from the scope at render, and the
  // address-held selection is applied in an effect of the same commit: read a
  // tick late, a request made under one selection went out with the next one
  // and its answer was cached under the wrong key. Nothing here awaits.
  { synchronous: true },
);

// Response interceptor - handle token refresh
const AUTH_ENDPOINTS = [
  "/auth/login/",
  "/auth/refresh/",
  "/auth/register/",
  "/msps/onboard/",
  "/invitations/redeem/",
];

/**
 * 401 on these URLs is an answer the calling page handles, never a reason to
 * refresh, sign out or redirect (which would wipe the login form, or take an
 * invitation out of the address bar).
 *
 * `/auth/logout/`: a 401 on sign-out is an already-dead refresh token.
 * `/invitations/validate/`: a stored session whose account is gone; the
 * invitation page drops it in place and validates again anonymously.
 */
const ALLOW_ANONYMOUS_401 = [
  "/sso-connections/lookup",
  "/auth/logout/",
  "/invitations/validate/",
];

/**
 * 403 codes the initiating page renders inline, so the global permission toast
 * is suppressed for them. `step_up_required` is a prompt to re-authenticate,
 * not a permission denial: toasting "You don't have permission" alongside the
 * page's own inline prompt would contradict it and misstate the cause.
 *
 * `organization_rejected` is the same shape -- the account's organisation was
 * refused at approval, which the login page and the rejected screen both state
 * properly, and "you don't have permission to perform this action" describes
 * neither what happened nor what to do about it. `account_inactive` is the
 * third: an administrator turned the account off, the login page says so in a
 * banner that stays, and the generic denial toasted over it would name neither
 * who decided nor what to do. `organization_suspended` is the fourth, one
 * column over from that: the organisation was switched off rather than the
 * account, and the same banner names the same remedy.
 *
 * `sso_managed` is the same shape one step further out. It is raised on the
 * password-reset pre-flight check, which runs on page load for an anonymous
 * visitor who has done nothing yet -- so "You don't have permission to perform
 * this action" is both wrong about the cause and addressed to somebody who
 * never performed an action. The page states the real remedy, which is to sign
 * in through the identity provider.
 *
 * `sso_required` is a password sign-in to a domain that enforces SSO. The
 * login page shows the SSO banner with the way in; the generic denial over it
 * would name the wrong cause.
 *
 * `mfa_locked` (too many wrong second-factor codes) and `mfa_required_for_role`
 * (turning two-factor off for a role that requires it) are explained by the
 * MFA pages that receive them.
 *
 * Every other 403 still toasts.
 */
const INLINE_403_CODES = [
  "step_up_required",
  "organization_rejected",
  "account_inactive",
  "organization_suspended",
  "sso_managed",
  "sso_required",
  "impersonation_not_allowed",
  "mfa_locked",
  "mfa_required_for_role",
  // Override refusals (`schedulingOverrides.ts`). The schedule page shows the
  // server's own sentence, which says what the limit is or why a default
  // cannot be overridden; "you don't have permission" is wrong for both.
  "duration_too_long",
  "system_schedule",
  // A change to what a live schedule does to machines, by someone who may
  // edit schedules but not start and stop machines. The schedule page says
  // the server's sentence: the change can be proposed, not published.
  "live_schedule_needs_start_stop",
];

const CREDENTIAL_VALIDATE_401 = /\/credentials\/[^/]+\/validate\/?$/;

/**
 * Error codes that mean *the cloud provider said no*, not *your session ended*.
 *
 * A 401 carrying one of these is a *third-party cloud* credential (AWS/Azure/GCP)
 * failing — not the user's EverythingCloud session. Running it through session
 * refresh replays the original request, which for a Start/Stop is a second
 * state-changing provider call, and logs the user out when refresh fails for
 * something their login had nothing to do with. Two body shapes carry it: the
 * instance-control action result puts `code` at the top level, every other view
 * raises `CredentialExpiredError` through the `{error: {code}}` envelope.
 */
const PROVIDER_ERROR_CODES = new Set([
  "credential_expired",
  "credential_invalid",
  "unsupported",
  "sdk_error",
  "action_failed",
]);

/** Whether a response is the provider refusing, rather than an auth failure. */
function isProviderError(error: AxiosError): boolean {
  const data = error.response?.data as { code?: unknown } | undefined;
  const code =
    typeof data?.code === "string" ? data.code : extractErrorCode(error);
  return typeof code === "string" && PROVIDER_ERROR_CODES.has(code);
}

/**
 * The sentence a refusal carries: the envelope's `message`, or a bare DRF
 * `detail` from a view that answered outside the envelope.
 */
function refusalMessage(data: unknown): string | undefined {
  const body = data as
    | { error?: { message?: unknown }; detail?: unknown }
    | undefined;
  const message = body?.error?.message ?? body?.detail;
  return typeof message === "string" ? message : undefined;
}

/** The envelope's `error.code`, when it is a single string. */
function refusalCode(data: unknown): string | undefined {
  const code = (data as { error?: { code?: unknown } } | undefined)?.error
    ?.code;
  return typeof code === "string" ? code : undefined;
}

/** Shared promise so concurrent 401s coalesce into a single refresh call. */
let refreshPromise: Promise<{ access: string; refresh: string }> | null = null;

/** `endSession` carries the reentrancy guard this used to hold locally. */
const forceLogout = endSession;

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };
    const requestUrl = originalRequest?.url ?? "";

    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    if (!error.response) {
      toast.error("Unable to reach the server. Please check your connection.", {
        id: "network-error",
      });
      return Promise.reject(error);
    }

    if (
      error.response.status === 401 &&
      (ALLOW_ANONYMOUS_401.some((path) => requestUrl.includes(path)) ||
        CREDENTIAL_VALIDATE_401.test(requestUrl) ||
        isProviderError(error))
    ) {
      return Promise.reject(error);
    }

    const isAuthEndpoint = AUTH_ENDPOINTS.some((ep) => requestUrl.includes(ep));

    // The server refusing the scope the store stamped on this request. Every
    // later request would be refused the same way, so the selection falls back
    // to the caller's default (with a notice) and the address follows it.
    const sent = originalRequest
      ? stampedFromBar.get(originalRequest)
      : undefined;
    if (
      sent &&
      isScopeRefusal(
        error.response.status,
        refusalMessage(error.response.data),
        sent,
        refusalCode(error.response.data),
      )
    ) {
      refuseScopeFromServer(sent);
    }

    if (error.response.status === 403) {
      // A 403 is the server saying this session may do less than the browser
      // thinks. Usually that is a page the user was never allowed on, and the
      // refresh is a no-op; when an administrator has just changed their role
      // it is the earliest possible notice, and waiting for the next poll would
      // leave stale navigation on screen in the one direction that matters.
      //
      // Fired and forgotten so the caller's own error handling is unaffected,
      // and only where there is a session whose role could have changed.
      //
      // Signed out there is none, and re-reading anyway was actively harmful:
      // `sso_required` is a real 403 from `/auth/login/`, and the `/auth/me/`
      // it triggered went out with no token, came back 401, matched neither
      // `ALLOW_ANONYMOUS_401` nor `AUTH_ENDPOINTS`, and reached `forceLogout()`
      // -- a hard reload of the login page that destroyed the SSO banner the
      // reader needed. The auth endpoints are excluded for the same reason:
      // their 403s are answers about a sign-in attempt, not about a role.
      // `/auth/me/` itself is excluded so a 403 there cannot recurse.
      if (
        useAuthStore.getState().isAuthenticated &&
        !isAuthEndpoint &&
        !requestUrl.includes("/auth/me/")
      ) {
        void useAuthStore.getState().fetchMe();
      }

      // Only a *write* gets a toast. A 403 on a GET is a read denial the page
      // hit on its own initiative — usually just by being opened — and toasting
      // "you don't have permission to perform this action" for something the
      // user never did reads as an error they caused. Pages render read
      // denials inline instead; see `RoleGate`.
      const method = (originalRequest?.method ?? "get").toLowerCase();
      const isWrite =
        method !== "get" && method !== "head" && method !== "options";
      if (
        isWrite &&
        !INLINE_403_CODES.includes(extractErrorCode(error) ?? "")
      ) {
        toast.error("You don't have permission to perform this action.", {
          id: "scope-403",
        });
      }
      return Promise.reject(error);
    }

    if (
      error.response.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      originalRequest._retry = true;
      const { refreshToken, setTokens } = useAuthStore.getState();

      if (refreshToken) {
        try {
          if (!refreshPromise) {
            refreshPromise = axios
              .post(`${API_BASE_URL}/auth/refresh/`, {
                refresh: refreshToken,
              })
              .then((response) => response.data)
              .finally(() => {
                refreshPromise = null;
              });
          }

          const { access, refresh } = await refreshPromise;
          setTokens(access, refresh);

          originalRequest.headers.Authorization = `Bearer ${access}`;
          // Replayed, power actions included. A session 401 is answered by
          // authentication before any view runs, so the first request never
          // reached a provider. The provider's own refusal is
          // `credential_expired`, which `isProviderError` turned away above.
          return apiClient(originalRequest);
        } catch (refreshError) {
          // A session past its absolute lifetime is refused with its own
          // code, and the login page says so rather than appearing unbidden.
          forceLogout(
            extractErrorCode(refreshError) === "session_expired"
              ? "session_expired"
              : undefined,
          );
          return Promise.reject(error);
        }
      }

      forceLogout();
    }

    // A 401 that survives the retry. `_retry` is already true, so the block
    // above is skipped and this used to fall straight through: the session was
    // refreshed successfully and the request was refused anyway, which means
    // the token is valid and the server is declining the *user*. Nothing ended
    // the session, so the SPA sat there refreshing a token that authorises
    // nothing — an empty screen with no toast, no redirect and no explanation.
    // Signing out lands the reader on /login, which says why.
    if (
      error.response.status === 401 &&
      originalRequest?._retry &&
      !isAuthEndpoint
    ) {
      forceLogout();
    }

    return Promise.reject(error);
  },
);

// API Response types
export interface ApiResponse<T> {
  data: T;
  message?: string;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface ApiError {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
    request_id?: string;
  };
}
